import { EditorState, Modifier, RichUtils } from "draft-js";

import withProtocol from "libs/helpers/withProtocol";

const handleEditorStateChange = (editor, core) => {
  if (!editor) return;

  const { editorState } = editor;
  const { id = "text", setContent, className } = core || {};

  const contentState = editorState.getCurrentContent();
  const blockMap = contentState.getBlockMap();
  let contentChanged = false;
  let newContentState = contentState;

  blockMap.forEach((block) => {
    const blockType = block.getType();

    if (blockType.startsWith("header-")) {
      const blockKey = block.getKey();
      newContentState = Modifier.setBlockType(
        newContentState,
        newContentState.getSelectionAfter().merge({
          anchorKey: blockKey,
          anchorOffset: 0,
          focusKey: blockKey,
          focusOffset: block.getLength(),
          isBackward: false,
        }),
        "unstyled",
      );
      contentChanged = true;
    }
  });

  if (contentChanged) {
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      "change-block-type",
    );

    if (typeof setContent === "function") setContent(newEditorState);

    const selection = newEditorState.getSelection();
    const updatedBlockMap = newContentState.getBlockMap();
    const blockMapArray = updatedBlockMap.toArray();
    const totalBlocks = blockMapArray.length;

    const cursorBlockKey = selection.getAnchorKey();
    const cursorBlockIndex = blockMapArray.findIndex(
      (block) => block.getKey() === cursorBlockKey,
    );

    if (totalBlocks > 0 && cursorBlockIndex >= totalBlocks - 7) {
      setTimeout(() => {
        const editorElement = document.querySelector(
          `#${id} .${className}__editor`,
        );

        if (editorElement) {
          editorElement.scrollTop = editorElement.scrollHeight;
        }
      }, 0);
    }
  } else {
    if (typeof setContent === "function") setContent(editorState);

    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const blockMap = content.getBlockMap();
    const blockMapArray = blockMap.toArray();
    const totalBlocks = blockMapArray.length;

    const cursorBlockKey = selection.getAnchorKey();
    const cursorBlockIndex = blockMapArray.findIndex(
      (block) => block.getKey() === cursorBlockKey,
    );

    if (totalBlocks > 0 && cursorBlockIndex >= totalBlocks - 7) {
      setTimeout(() => {
        const editorElement = document.querySelector(
          `#editor--${id} .${className}__editor`,
        );

        if (editorElement) {
          editorElement.scrollTop = editorElement.scrollHeight;
        }
      }, 0);
    }
  }
};

const handleKeyCommand = (editor, core) => {
  if (!editor) return;

  const { command, editorState } = editor;
  const { setContent } = core || {};

  if (command === "backspace") {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(selection.getStartKey());
    const blockType = currentBlock.getType();
    const cursorPosition = selection.getStartOffset();

    if (
      (blockType === "unordered-list-item" ||
        blockType === "ordered-list-item") &&
      cursorPosition === 0 &&
      currentBlock.getText().length === 0
    ) {
      const newContentState = Modifier.setBlockType(
        contentState,
        selection,
        "unstyled",
      );

      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        "change-block-type",
      );

      if (typeof setContent === "function") setContent(newEditorState);

      return "handled";
    }

    if (cursorPosition === 0 && currentBlock.getText().length === 0) {
      const currentInlineStyle = editorState.getCurrentInlineStyle();

      if (currentInlineStyle.size > 0) {
        let newEditorState = editorState;

        currentInlineStyle.forEach((style) => {
          newEditorState = EditorState.setInlineStyleOverride(
            newEditorState,
            newEditorState.getCurrentInlineStyle().remove(style),
          );
        });

        const newContentState = Modifier.removeInlineStyle(
          contentState,
          selection,
          currentInlineStyle,
        );

        newEditorState = EditorState.push(
          newEditorState,
          newContentState,
          "change-inline-style",
        );

        if (typeof setContent === "function") setContent(newEditorState);

        return "handled";
      }
    }
  }

  if (
    [
      "bold",
      "italic",
      "underline",
      "code",
      "BOLD",
      "ITALIC",
      "UNDERLINE",
      "CODE",
    ].includes(command)
  ) {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      if (typeof setContent === "function") setContent(newState);
      return "handled";
    }
  }

  return "not-handled";
};

const handleBeforeInput = (editor, core) => {
  if (!editor) return;

  const { character, editorState } = editor;
  const { setContent } = core || {};

  if (character === " ") {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(selection.getStartKey());
    const text = currentBlock.getText();
    const cursorPosition = selection.getStartOffset();

    let startPos = cursorPosition;
    while (startPos > 0 && !/\s/.test(text[startPos - 1])) {
      startPos--;
    }

    const word = text.slice(startPos, cursorPosition);
    const urlRegex = /^(https?:\/\/[^\s]+)|(www\.[^\s]+\.[^\s]+)$/;

    if (urlRegex.test(word)) {
      const url = withProtocol(word);

      const urlSelection = selection.merge({
        anchorOffset: startPos,
        focusOffset: cursorPosition,
      });

      let newContentState = Modifier.replaceText(
        contentState,
        urlSelection,
        word,
      );

      newContentState = newContentState.createEntity("LINK", "MUTABLE", {
        url: url,
        targetOption: "_blank",
      });

      const entityKey = newContentState.getLastCreatedEntityKey();

      newContentState = Modifier.applyEntity(
        newContentState,
        urlSelection,
        entityKey,
      );

      const newSelection = urlSelection.merge({
        anchorOffset: cursorPosition,
        focusOffset: cursorPosition,
      });

      newContentState = Modifier.insertText(newContentState, newSelection, " ");

      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        "apply-entity",
      );

      const finalSelection = newEditorState.getSelection().merge({
        anchorOffset: cursorPosition + 1,
        focusOffset: cursorPosition + 1,
      });

      const finalEditorState = EditorState.forceSelection(
        newEditorState,
        finalSelection,
      );

      if (typeof setContent === "function") setContent(finalEditorState);

      return "handled";
    }
  }

  return "not-handled";
};

const handlePastedText = (editor, core) => {
  if (!editor) return;

  const { text: originalText, editorState } = editor;
  const { setContent } = core || {};

  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+\.[^\s]+)/g;

  if (urlRegex.test(originalText)) {
    const matches = originalText.match(urlRegex);

    if (matches && matches.length > 0) {
      try {
        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();

        let contentStateWithoutEntities = contentState;

        if (selection.getStartOffset() !== selection.getEndOffset()) {
          contentStateWithoutEntities = Modifier.applyEntity(
            contentState,
            selection,
            null,
          );
        }

        const contentStateWithText = Modifier.replaceText(
          contentStateWithoutEntities,
          selection,
          originalText,
        );

        let tempEditorState = EditorState.push(
          editorState,
          contentStateWithText,
          "insert-text",
        );

        let finalContentState = contentStateWithText;
        const insertionOffset = selection.getStartOffset();
        let processedText = originalText;

        matches.forEach((matchedUrl) => {
          const url = withProtocol(matchedUrl);

          const urlStartPos = processedText.indexOf(matchedUrl);
          const urlEndPos = urlStartPos + matchedUrl.length;

          const urlSelection = tempEditorState.getSelection().merge({
            anchorOffset: insertionOffset + urlStartPos,
            focusOffset: insertionOffset + urlEndPos,
          });

          finalContentState = finalContentState.createEntity(
            "LINK",
            "MUTABLE",
            {
              url: url,
              targetOption: "_blank",
            },
          );

          const entityKey = finalContentState.getLastCreatedEntityKey();

          finalContentState = Modifier.applyEntity(
            finalContentState,
            urlSelection,
            entityKey,
          );

          processedText = processedText.replace(
            matchedUrl,
            " ".repeat(matchedUrl.length),
          );
        });

        const finalEditorState = EditorState.push(
          tempEditorState,
          finalContentState,
          "apply-entity",
        );

        const newSelection = finalEditorState.getSelection().merge({
          anchorOffset: selection.getStartOffset() + originalText.length,
          focusOffset: selection.getStartOffset() + originalText.length,
        });

        const editorStateWithCursor = EditorState.forceSelection(
          finalEditorState,
          newSelection,
        );

        if (typeof setContent === "function") setContent(editorStateWithCursor);

        return true;
      } catch (err) {
        console.log(err);
        return false;
      }
    }
  }

  return false;
};

export {
  handleEditorStateChange,
  handleKeyCommand,
  handleBeforeInput,
  handlePastedText,
};
